import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface ChatMessage {
  text: string;
  sender: 'user' | 'ai';
}

const SESSION_KEY = 'mh3-chat-session-id';

@Injectable({ providedIn: 'root' })
export class N8nChatService {
  private sessionId: string;

  constructor(private http: HttpClient) {
    this.sessionId = sessionStorage.getItem(SESSION_KEY) || this.generateSessionId();
    sessionStorage.setItem(SESSION_KEY, this.sessionId);
  }

  private generateSessionId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  resetSession(): void {
    this.sessionId = this.generateSessionId();
    sessionStorage.setItem(SESSION_KEY, this.sessionId);
  }

  sendMessage(message: string): Observable<string> {
    const url = environment.n8nWebhookUrl;
    if (!url) {
      return of('n8nWebhookUrl ist nicht konfiguriert.');
    }

    const body: Record<string, string> = {
      action: 'sendMessage',
      sessionId: this.sessionId,
      chatInput: message,
    };

    return this.http.post(url, body, {
      responseType: 'text',
      headers: { 'Content-Type': 'application/json' },
    }).pipe(
      map(response => this.extractMessage(response)),
      catchError(err => {
        console.error('n8n Chat Fehler:', err);
        return of('Verbindungsfehler. Bitte später erneut versuchen.');
      })
    );
  }

  private extractMessage(response: any): string {
    if (typeof response === 'string') {
      try {
        const parsed = JSON.parse(response);
        return this.getTextFromResponse(parsed);
      } catch {
        return response;
      }
    }
    return this.getTextFromResponse(response);
  }

  private getTextFromResponse(obj: any): string {
    if (!obj) return '';
    if (typeof obj.output === 'string') return obj.output;
    if (obj.output?.message) return obj.output.message;
    if (obj.output?.text) return obj.output.text;
    if (obj.message) return obj.message;
    if (obj.text) return obj.text;
    if (obj.output && typeof obj.output === 'object') {
      const msg = obj.output.output || obj.output.chatOutput || obj.output.response;
      if (msg) return typeof msg === 'string' ? msg : JSON.stringify(msg);
    }
    return typeof obj === 'string' ? obj : JSON.stringify(obj);
  }
}
